import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../../../firebase-config';
import { geminiGenerateJson } from '../../geminiService';
import { distillEvidenceSchemaV1 } from './distillEvidence';
import { AI_PIPELINE_SCHEMA_V1 } from '../../../constants/aiPipelineModes';
import { normalizeExtractionStations } from '../../../utils/normalizeVerification';

/**
 * Schema v1 refinery orchestration (ringfenced).
 * Still filename/title based until per-file content jobs land; verification lens vs distilled fact sheet.
 *
 * @param {string} schoolId
 * @param {{ updateRefineryStep: Function }} deps — from adminServices to avoid circular imports
 */
export async function runRefineryExtractionSchemaV1(schoolId, deps) {
  const { updateRefineryStep } = deps;
  const reportRef = doc(db, 'reports', schoolId);

  await updateRefineryStep(schoolId, 'evidence', 1, 'extracting');

  const distilledData = await distillEvidenceSchemaV1(schoolId);

  await updateRefineryStep(schoolId, 'evidence', 3, 'synthesising');

  const prompt = `
You are the IFIP Evidence Analysis Engine (schema_v1).
Analyse this DISTILLED title-based fact sheet for verification — not complementary storytelling.

INSTITUTIONAL OVERVIEW: ${distilledData.anonymizedSummary || ''}
DISTILLED FACT SHEET: ${JSON.stringify(distilledData.factSheet || [])}

TASK: For EACH of the 7 IFIP Stations (01-07), provide a verification lens based ONLY on these distilled title facts.

- 2 "Evidence Anchors": Specific title-based points that could support typical station claims.
- 1 "Evidence Gap": A claim usually needed for this station that is NOT supported by these titles/files.
- Note photographic entries as human-review-only, never as verified content.
- CRITICAL: "anchors" and "gaps" MUST be arrays of short plain strings only. Do NOT return factSheet objects.

OUTPUT: Return ONLY raw JSON:
{ "stations": { "01": { "anchors": ["string", "string"], "gaps": ["string"] }, "02": { "anchors": ["string", "string"], "gaps": ["string"] }, "03": { "anchors": ["string", "string"], "gaps": ["string"] }, "04": { "anchors": ["string", "string"], "gaps": ["string"] }, "05": { "anchors": ["string", "string"], "gaps": ["string"] }, "06": { "anchors": ["string", "string"], "gaps": ["string"] }, "07": { "anchors": ["string", "string"], "gaps": ["string"] } }, "pipeline": "schema_v1" }
`;

  const extraction = await geminiGenerateJson(prompt);

  const updates = {};
  const stations = normalizeExtractionStations(extraction.stations || {});
  Object.keys(stations).forEach((sId) => {
    updates[`stationAnalysis.${sId}.verification`] = stations[sId];
  });
  updates['meta.refinery.evidence'] = { step: 4, label: 'stored', pipeline: AI_PIPELINE_SCHEMA_V1 };
  updates['meta.aiPipeline'] = AI_PIPELINE_SCHEMA_V1;

  await updateDoc(reportRef, updates);

  // Ensure report exists path — if update fails when missing, caller handles
  const reportSnap = await getDoc(reportRef);
  if (!reportSnap.exists()) {
    throw new Error('Report document missing; initialize report before refinery.');
  }

  return extraction;
}